/**
 * SP-MV2-1198: client-side overlap validation for idle exemption windows.
 * Mirrors the server-side check in IdleExemptPersistenceService.validateNonOverlapping.
 *
 * A schedule is `{ dayOfWeek: 1..7, startTime: 'HH:mm', endTime: 'HH:mm' }`.
 * If start > end the slot crosses midnight — the head belongs to `dayOfWeek`,
 * the tail belongs to `dayOfWeek + 1` (Sunday wraps to 1).
 */

const parseSeconds = (hhmm) => {
  if (!hhmm || typeof hhmm !== 'string') return null;
  const [h, m] = hhmm.split(':').map((s) => parseInt(s, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 3600 + m * 60;
};

/**
 * Returns the set of `dayOfWeek` values that have overlapping windows.
 * Empty set means no overlap. Days are 1..7 (Sunday=1).
 */
export function findOverlappingDays(schedules) {
  if (!Array.isArray(schedules) || schedules.length === 0) return new Set();

  // Expand each slot into 1 or 2 segments keyed by the calendar day the segment lands on.
  const byDay = new Map(); // day → [ [startSec, endSec), ... ]
  for (const s of schedules) {
    if (!s || !s.dayOfWeek) continue;
    const startSec = parseSeconds(s.startTime);
    const endSec = parseSeconds(s.endTime);
    if (startSec == null || endSec == null || startSec === endSec) continue;

    const day = s.dayOfWeek;
    const nextDay = day === 7 ? 1 : day + 1;

    if (startSec < endSec) {
      // Same-day slot
      if (!byDay.has(day)) byDay.set(day, []);
      byDay.get(day).push([startSec, endSec]);
    } else {
      // Crosses midnight: head on this day, tail on the next
      if (!byDay.has(day)) byDay.set(day, []);
      byDay.get(day).push([startSec, 86400]);
      if (endSec > 0) {
        if (!byDay.has(nextDay)) byDay.set(nextDay, []);
        byDay.get(nextDay).push([0, endSec]);
      }
    }
  }

  const overlappingDays = new Set();
  for (const [day, segs] of byDay.entries()) {
    if (segs.length < 2) continue;
    const sorted = [...segs].sort((a, b) => a[0] - b[0]);
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i][0] < sorted[i - 1][1]) {
        overlappingDays.add(day);
        break;
      }
    }
  }
  return overlappingDays;
}
